import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import MatchingDetailsCard from "./matchingDetailsCard";
import { useCommonStyles } from "./styles";
import EDIT_ICON from "./images/editIcon.svg";
import INSIGHTS_BLUE_ICON from "./images/insights-blue.svg";
import INSIGHTS_YELLOW_ICON from "./images/insights-yellow.svg";
import INSIGHTS_FILLED_ICON from "./images/insights-filled.svg";
import INSIGHTS_YELLOW_FILLED_ICON from "./images/insights-yellow-filled.svg";

import MatchingDetailsInsights from "./matchingDetailsInsights";
import MatchingDetailsEditGrade from "./matchingDetailsEditGrade";
import { getUserPermission } from "controllers/matchingDetails";

const CIRCLE_COLORS_MAP = {
  A: "#00CC66",
  B: "#33CCFF",
  C: "#FFCC00",
  D: "#FF6600",
  E: "#FF0000",
  F: "#808080",
};

const useStyles = makeStyles((theme) => ({
  scoreCardContainer: {
    height: 380,
    marginTop: 32,
    marginBottom: 32,
  },
  overallScoreInnerCircle: {
    background: (props) => (props.mode === "dark" ? "#282A4A" : "#F5F6F6"),
    borderRadius: "100%",
    top: "calc(50% - 72px)",
    left: 0,
    bottom: 0,
    right: 0,
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 144,
    height: 144,
    margin: "0 auto",
    flexDirection: "column",
  },
  overallScoreGrade: {
    color: (props) => (props.mode === "dark" ? "#FFF" : "#3B3D3F"),
    textAlign: "center",
    fontSize: 64,
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "64px",
  },
  editIconContainer: {
    position: "absolute",
    right: "15%",
    top: "24px",
    color: "#428BFA",
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    display: "flex",
    cursor: "pointer",

    "& > img": {
      paddingRight: 8,
    },
  },
}));

const ScoreCircle = ({
  containerPadding = "32px",
  circleColor = "#00CC66",
  circleSize = 256,
  circleThickness = 4,
  circleValue = 0,
  mode,
  children,
}) => {
  return (
    <Box position="relative" display="inline-flex" padding={containerPadding}>
      <CircularProgress
        variant="determinate"
        value={circleValue}
        size={circleSize}
        thickness={circleThickness}
        style={{ color: circleColor, zIndex: 1 }}
      />
      <CircularProgress
        variant="determinate"
        value={100}
        size={circleSize}
        thickness={circleThickness}
        style={{
          color: mode === "dark" ? "#282A4A" : "#F5F6F6",
          position: "absolute",
        }}
      />
      {children}
    </Box>
  );
};

const useScoreSectionStyles = makeStyles((theme) => ({
  scoreSectionContainer: {
    height: 56,
    borderRadius: 8,
    border: (props) =>
      `1px solid ${theme.palette.matchingDetailsPage[props.mode].border}`,
    background: (props) => theme.palette.matchingDetailsPage[props.mode].bg,
    borderLeft: (props) =>
      `9px solid ${theme.palette.matchingDetailsPage[props.mode].border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
  },
  sectionInnerGrade: {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 600,
    color: (props) => (props.mode === "dark" ? "#FFF" : "#000"),
  },
  insightsIcon: {
    background: (props) =>
      props.mode === "dark" ? "rgba(255, 213, 23, 0.07)" : "#EEF5FE",
    padding: 4,
    marginLeft: 12,
    cursor: "pointer",
    borderRadius: "100%",
  },
  insightGridContainer: {
    width: 780,
    borderRadius: 8,
    border: (props) =>
      `1px solid ${props.mode === "dark" ? "#282A4A" : "#F1F1F1"}`,
    padding: 16,
    background: (props) => (props.mode === "dark" ? "#191826" : "#FFF"),
  },
  insightContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const ScoreSection = ({
  title = "",
  grade = "",
  score = 0,
  insights = false,
  mode,
  circleColor,
  insightsText = [],
}) => {
  const [insightsOpen, setInsightsOpen] = useState(false);
  const classes = useScoreSectionStyles({ mode });
  const commonClasses = useCommonStyles({ mode });

  const ModalInsight = () => {
    return (
      <Grid container classes={{ root: classes.insightGridContainer }}>
        <Grid xs={12} classes={{ root: classes.insightContainer }}>
          <Box display={"flex"} alignItems={"center"}>
            <img
              src={
                mode === "dark"
                  ? INSIGHTS_YELLOW_FILLED_ICON
                  : INSIGHTS_FILLED_ICON
              }
              alt="Insights Icon"
            />
            <Typography
              classes={{ root: commonClasses.fontMediumBold }}
              style={{ marginLeft: 14 }}
            >
              Insights
            </Typography>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <Typography
              classes={{ root: commonClasses.fontMediumBold }}
              style={{ marginRight: 24 }}
            >
              Grade
            </Typography>
            <ScoreCircle
              containerPadding="0"
              circleColor={circleColor}
              circleSize={36}
              circleThickness={4}
              circleValue={score}
              mode={mode}
            >
              <Typography classes={{ root: classes.sectionInnerGrade }}>
                {grade}
              </Typography>
            </ScoreCircle>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <div className={classes.scoreSectionContainer}>
      <Box display={"flex"}>
        <Typography classes={{ root: commonClasses.fontMediumBold }}>
          {title}
        </Typography>
        {insights && (
          <img
            src={mode === "dark" ? INSIGHTS_YELLOW_ICON : INSIGHTS_BLUE_ICON}
            alt={`${title} Insights`}
            className={classes.insightsIcon}
            onClick={() => setInsightsOpen(true)}
          />
        )}
      </Box>

      <ScoreCircle
        containerPadding="0"
        circleColor={circleColor}
        circleSize={36}
        circleThickness={4}
        circleValue={score}
        mode={mode}
      >
        <Typography classes={{ root: classes.sectionInnerGrade }}>
          {grade}
        </Typography>
      </ScoreCircle>
      {insights && insightsOpen && (
        <MatchingDetailsInsights
          isOpen={insightsOpen}
          handleClose={() => setInsightsOpen(false)}
          mode={mode}
          title={title}
          ModalInsight={ModalInsight}
          insightsText={insightsText?.[0]}
        />
      )}
    </div>
  );
};

const MatchingDetailsScoreCard = ({
  overallScore = 0,
  overallScoreGrade = "",
  workHistoryScore = 0,
  workHistoryGrade = "",
  workHistoryInsights = [],
  skillScore = 0,
  skillGrade = "",
  skillInsights = [],
  jobTitleScore = 0,
  jobTitleGrade = "",
  jobTitleInsights = [],
  assesmentScore = 0,
  assesmentGrade = "",
  assesmentInsights = [],
  chatPreScreeningScore = 0,
  chatPreScreeningGrade = "",
  chatPreScreeningInsights = [],
  skillsAssessmentScore = 0,
  skillsAssessmentGrade = "",
  skillsAssessmentInsights = [],
  upgradeOverallGrade,
  mode,
}) => {
  const [editGradeOpen, setEditGradeOpen] = useState(false);
  const classes = useStyles({ mode });
  const scoreSectionClasses = useScoreSectionStyles({ mode });
  const isAdmin = getUserPermission();

  return (
    <MatchingDetailsCard rootClass={classes.scoreCardContainer} mode={mode}>
      <Grid container style={{ height: "100%" }}>
        <Grid item xs={4}>
          <Box position="relative">
            {isAdmin && (
              <Box
                className={classes.editIconContainer}
                onClick={() => setEditGradeOpen(true)}
              >
                <img src={EDIT_ICON} alt="Edit" /> Edit
              </Box>
            )}

            <ScoreCircle
              circleValue={overallScore}
              mode={mode}
              circleColor={CIRCLE_COLORS_MAP[overallScoreGrade]}
            >
              <div className={classes.overallScoreInnerCircle}>
                <Typography classes={{ root: classes.overallScoreGrade }}>
                  {overallScoreGrade}
                </Typography>
                <Typography
                  variant="body1"
                  style={{ color: mode === "dark" ? "#FFF" : "#000" }}
                >
                  Overall Score
                </Typography>
              </div>
            </ScoreCircle>
          </Box>
        </Grid>
        <Grid item xs={8}>
          <Box
            display="flex"
            flexDirection="column"
            height="calc(100% - 48px)"
            justifyContent="space-between"
            padding="24px 0"
          >
            <ScoreSection
              mode={mode}
              title="Work History"
              grade={workHistoryGrade}
              score={workHistoryScore}
              circleColor={CIRCLE_COLORS_MAP[workHistoryGrade]}
              insightsText={workHistoryInsights}
              insights
            />
            <ScoreSection
              mode={mode}
              title="Skills"
              grade={skillGrade}
              score={skillScore}
              circleColor={CIRCLE_COLORS_MAP[skillGrade]}
              insightsText={skillInsights}
              insights
            />
            <ScoreSection
              mode={mode}
              title="Job Title"
              grade={jobTitleGrade}
              score={jobTitleScore}
              circleColor={CIRCLE_COLORS_MAP[jobTitleGrade]}
              insightsText={jobTitleInsights}
              insights
            />
            <ScoreSection
              mode={mode}
              title="Assessments"
              grade={assesmentGrade}
              score={assesmentScore}
              circleColor={CIRCLE_COLORS_MAP[assesmentGrade]}
              insightsText={assesmentInsights}
            />
            {chatPreScreeningScore ? (
              <ScoreSection
                mode={mode}
                title="AI Pre Screening"
                grade={chatPreScreeningGrade}
                score={chatPreScreeningScore}
                circleColor={CIRCLE_COLORS_MAP[chatPreScreeningGrade]}
                insightsText={chatPreScreeningInsights}
              />
            ) : null}
            {skillsAssessmentScore ? (
              <ScoreSection
                mode={mode}
                title="Skills Assessment"
                grade={skillsAssessmentGrade}
                score={skillsAssessmentScore}
                circleColor={CIRCLE_COLORS_MAP[skillsAssessmentGrade]}
                insightsText={skillsAssessmentInsights}
              />
            ) : null}
          </Box>
        </Grid>
      </Grid>
      {editGradeOpen && (
        <MatchingDetailsEditGrade
          title="Edit Match Grade"
          mode={mode}
          isOpen={editGradeOpen}
          handleClose={() => setEditGradeOpen(false)}
          grade={overallScoreGrade}
          upgradeOverallGrade={upgradeOverallGrade}
          CurrentGrade={
            <ScoreCircle
              containerPadding="0"
              circleSize={36}
              circleThickness={4}
              circleValue={overallScore}
              mode={mode}
              circleColor={CIRCLE_COLORS_MAP[overallScoreGrade]}
            >
              <Typography
                classes={{ root: scoreSectionClasses.sectionInnerGrade }}
              >
                {overallScoreGrade}
              </Typography>
            </ScoreCircle>
          }
        />
      )}
    </MatchingDetailsCard>
  );
};

export default MatchingDetailsScoreCard;
